import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '@services/auth.service';
import api from '@services/api/api.service';
import { logger } from '@utils/logger';
import { fetchCurrentSubscription, clearSubscription } from './subscriptionSlice';

const initialUser = authService.getUser();
const initialAuth = authService.isAuthenticated();
const initialPermissions = authService.getPermissions();



const initialState = {
  user: initialUser,
  isAuthenticated: initialAuth,
  loading: false,
  error: null,
  permissions: initialPermissions,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue, dispatch }) => {
    try {
      const response = await authService.login(credentials);
      // API service wraps response: response.data contains the actual backend response
      const userData = response.data.data || response.data;
      
      // Fetch user's subscription after successful login (only for students)
      if (userData?.user?.role === 'student') {
        dispatch(fetchCurrentSubscription());
      }
      
      // Fetch permissions after successful login (only for admins)
      if (userData?.user?.role === 'admin') {
        try {
          const permissionsResponse = await api.get('/admin-management/my-permissions');
          if (permissionsResponse.data.success) {
            userData.permissions = permissionsResponse.data.data || [];
            authService.setPermissions(userData.permissions);
          }
        } catch (error) {
          logger.error('Failed to fetch permissions during login', error);
          userData.permissions = [];
          authService.setPermissions([]);
        }
      }
      
      return userData;
    } catch (error) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue, dispatch }) => {
  try {
    await authService.logout();
    
    // Clear subscription data on logout
    dispatch(clearSubscription());
    
    return null;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getCurrentUser();
      // Response structure: { success: true, data: { user fields } }
      // Extract the actual user data from response.data
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.refreshToken();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPermissions = createAsyncThunk(
  'auth/fetchPermissions',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      
      if (auth.user?.role !== 'admin') {
        return [];
      }
      
      const response = await api.get('/admin-management/my-permissions');
      if (response.data.success) {
        const permissions = response.data.data || [];
        authService.setPermissions(permissions);
        return permissions;
      }
      return [];
    } catch (error) {
      logger.error('Failed to fetch permissions', error);
      return rejectWithValue(error.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.permissions = [];
    },
    setPermissions: (state, action) => {
      state.permissions = action.payload;
      authService.setPermissions(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        state.permissions = action.payload.permissions || [];
        logger.info('Login successful');
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        logger.error('Login failed', action.payload);
      })
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        // Don't set user or isAuthenticated - user needs to verify OTP first
        state.error = null;
        logger.info('Registration successful, awaiting OTP verification');
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        logger.error('Registration failed', action.payload);
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        state.permissions = [];
        logger.info('Logout successful');
      })
      .addCase(logout.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = [];
        logger.warn('Logout completed with errors');
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = [];
      })
      .addCase(refreshToken.fulfilled, (state) => {
        state.isAuthenticated = true;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = [];
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        logger.error('Failed to fetch permissions on refresh', action.payload);
        state.permissions = [];
      });
  },
});

export const { clearError, setUser, clearAuth, setPermissions } = authSlice.actions;

export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectPermissions = (state) => state.auth.permissions;

export default authSlice.reducer;
